import React from 'react';
import { Box, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { StepStatus } from './PropertyStepFlow';

interface StepIndicatorProps {
  status: StepStatus;
  icon: React.ComponentType<any>;
  size?: 'small' | 'medium' | 'large';
  onClick?: () => void;
  disabled?: boolean;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({
  status,
  icon: Icon,
  size = 'medium',
  onClick,
  disabled,
}) => {
  const theme = useTheme();

  const getSizeValues = () => {
    switch (size) {
      case 'small':
        return { boxSize: 32, iconSize: 18 };
      case 'large':
        return { boxSize: 48, iconSize: 28 };
      default:
        return { boxSize: 40, iconSize: 24 };
    }
  };

  const { boxSize, iconSize } = getSizeValues();

  const getColors = () => {
    if (disabled && status !== 'completed') {
      return {
        bgcolor: theme.palette.action.disabledBackground,
        color: theme.palette.text.disabled,
        borderColor: theme.palette.divider,
        borderStyle: 'dashed',
      };
    }

    switch (status) {
      case 'completed':
        return {
          bgcolor: theme.palette.success.main,
          color: 'white',
          borderColor: theme.palette.success.main,
          borderStyle: 'solid',
        };
      case 'in_progress':
        return {
          bgcolor: 'transparent',
          color: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
          borderStyle: 'solid',
        };
      case 'optional':
        return {
          bgcolor: 'transparent',
          color: theme.palette.grey[600],
          borderColor: theme.palette.grey[400],
          borderStyle: 'solid',
        };
      default: // not_started
        return {
          bgcolor: 'transparent',
          color: theme.palette.text.disabled,
          borderColor: theme.palette.divider,
          borderStyle: 'solid',
        };
    }
  };

  const colors = getColors();

  const isClickable = onClick && !disabled && status !== 'completed';

  return (
    <Box
      onClick={isClickable ? onClick : undefined}
      sx={{
        position: 'relative',
        width: boxSize,
        height: boxSize,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: colors.bgcolor,
        border: `2px ${colors.borderStyle} ${colors.borderColor}`,
        color: colors.color,
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
      }}
    >
      {status === 'completed' ? (
        <CheckIcon sx={{ fontSize: iconSize }} />
      ) : Icon ? (
        <Icon sx={{ fontSize: iconSize }} />
      ) : null}

    </Box>
  );
};

export default StepIndicator;